import React, {useEffect, useState} from 'react';
import {
    AppBar,
    Box,
    Button,
    CardMedia,
    CircularProgress,
    FormControl,
    FormControlLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Rating,
    Select,
    Stack,
    TextField,
    Toolbar,
    Typography,
    Card
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import StarIcon from '@mui/icons-material/Star';
import {AnyQuestion, Form, FormSubmission, FormAnswer, QuestionType, ShortTextQuestion} from 'crqlar-react-common/dist/models/forms/form';
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import {emailValidator, phoneNumberValidator} from 'crqlar-react-common/dist/utils/form.util';
import {FormService} from 'crqlar-react-common/dist/services/forms/form.service';
import {useMutation} from 'react-query';
import FormThemeProvider from '../FormThemeProvider';
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import MdRenderer from 'crqlar-react-common/dist/components/MdRenderer';
import {Media} from 'crqlar-react-common/dist/models/common/media.model';
import {getScaledUrl, MediaSize} from "crqlar-react-common/dist/utils/media.util";
import PhoneInputField from "crqlar-react-common/dist/components/PhoneInputField";
import PublicMediaUpload from "crqlar-react-common/dist/components/PublicMediaUpload";
import {useTranslation} from "react-i18next";

interface PreviewComponentProps {
    form: Form;
    onClose?: () => void;
    isPublic?: boolean;
    isEmbedded?: boolean;
}

const PreviewComponent: React.FC<PreviewComponentProps> = ({form, onClose, isPublic = false, isEmbedded = false}) => {
    const {t} = useTranslation();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [urlParams] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const paramsObj: Record<string, string> = {};
        params.forEach((value, key) => {
            paramsObj[key] = value;
        });
        return paramsObj;
    });
    const [emailValue, setEmailValue] = useState(urlParams.email || '');
    const [formAnswers, setFormAnswers] = useState<Map<string, string | string[] | boolean | number | Media | Media[]>>(new Map());
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Get current question
    const currentQuestion = form.questions?.[currentQuestionIndex];

    // Validate if current question has a valid answer
    const isCurrentQuestionValid = (): boolean => {
        if (!currentQuestion || !currentQuestion.required) {
            return true; // Not required, always valid
        }

        const answer = formAnswers.get(currentQuestion.id);

        switch (currentQuestion.type) {
            case QuestionType.WELCOME:
            case QuestionType.END_SCREEN:
                return true; // Welcome and End screens don't need validation

            case QuestionType.MULTIPLE_CHOICE:
            case QuestionType.PICTURE_CHOICE:
                // For multiple selection, answer is an array
                if (Array.isArray(answer)) {
                    return answer.length > 0;
                }
                // For single selection, answer is a string
                return typeof answer === 'string' && answer.trim() !== '';

            case QuestionType.DROPDOWN:
                return typeof answer === 'string' && answer.trim() !== '';

            case QuestionType.YES_NO:
                return typeof answer === 'boolean';

            case QuestionType.SHORT_TEXT:
                if (typeof answer !== 'string' || answer.trim() === '') {
                    return false;
                }
                // Validate based on purpose
                const shortTextQuestion = currentQuestion as ShortTextQuestion;
                if (shortTextQuestion.purpose === 'email') {
                    return !emailValidator(answer);
                }
                if (shortTextQuestion.purpose === 'tel') {
                    return !phoneNumberValidator(answer);
                }
                return true;
            case QuestionType.LONG_TEXT:
                return typeof answer === 'string' && answer.trim() !== '';

            case QuestionType.LEGAL:
                return answer === true; // Must explicitly accept

            case QuestionType.RATING:
                return typeof answer === 'number' && answer > 0;

            case QuestionType.UPLOAD_MEDIA:
                // For multiple uploads, answer is an array
                if (Array.isArray(answer)) {
                    return answer.length > 0;
                }
                // For single upload, answer is a Media object
                return answer !== undefined && answer !== null;

            default:
                return true;
        }
    };

    // Merge current question theme with form theme, question takes precedence
    const mergedTheme = currentQuestion?.theme ? {
        ...form.theme,
        palette: {
            ...form.theme?.palette,
            ...currentQuestion.theme.palette
        },
        typography: {
            ...form.theme?.typography,
            ...currentQuestion.theme.typography
        },
        shape: {
            ...form.theme?.shape,
            ...currentQuestion.theme.shape
        },
        backgroundMedia: currentQuestion.theme.backgroundMedia || form.theme?.backgroundMedia
    } : form.theme;

    const backgroundImageUrl = mergedTheme?.backgroundMedia?.mediaURI;
    const layout = currentQuestion?.layout || 'DEFAULT';

    // Check screen width for responsive layout
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Only apply side splits on screens >= 600px
    const isMobileScreen = windowWidth < 600;
    const effectiveLayout = (layout === 'LEFT_SPLIT' || layout === 'RIGHT_SPLIT') && isMobileScreen
        ? 'DEFAULT'
        : layout;
    const isSplitLayout = effectiveLayout !== 'DEFAULT';

    // Check if current question is a welcome screen with video
    const isWelcomeWithVideo = currentQuestion?.type === QuestionType.WELCOME && (currentQuestion as any).videoMedia;
    const welcomeVideoMedia = isWelcomeWithVideo ? (currentQuestion as any).videoMedia : null;

    // Special case: Welcome screen with video AND split layout - use video in split side instead of background
    const isWelcomeWithVideoAndSplit = isWelcomeWithVideo && isSplitLayout;

    // Determine flex direction and sizes based on layout
    const getLayoutStyles = () => {
        // For TOP_SPLIT in embedded mode, calculate image size based on formMinHeight
        let topSplitImageSize = '50%';
        if (effectiveLayout === 'TOP_SPLIT' && isEmbedded && mergedTheme?.shape?.formMinHeight) {
            const minHeightValue = mergedTheme.shape.formMinHeight;
            // Parse the numeric value from the formMinHeight string
            const numericValue = parseInt(minHeightValue, 10);
            if (!isNaN(numericValue)) {
                // Divide by 2 for the image size
                const imageHeight = numericValue / 2;
                topSplitImageSize = `${imageHeight}px`;
            } else {
                // Fallback to 300px if parsing fails
                topSplitImageSize = '200px';
            }
        } else if (effectiveLayout === 'TOP_SPLIT' && isEmbedded) {
            // Fallback to 300px if no formMinHeight is set
            topSplitImageSize = '200px';
        }

        switch (effectiveLayout) {
            case 'TOP_SPLIT':
                return {
                    flexDirection: 'column' as const,
                    imageSize: topSplitImageSize,
                    contentSize: '50%'
                };
            case 'LEFT_SPLIT':
                return {
                    flexDirection: 'row' as const,
                    imageSize: '50%',
                    contentSize: '50%'
                };
            case 'RIGHT_SPLIT':
                return {
                    flexDirection: 'row-reverse' as const,
                    imageSize: '50%',
                    contentSize: '50%'
                };
            default:
                return {
                    flexDirection: 'column' as const,
                    imageSize: '0%',
                    contentSize: '100%'
                };
        }
    };

    const layoutStyles = getLayoutStyles();

    const submitFormMutation = useMutation({
        mutationFn: async (submissionRequest: FormSubmission) => {
            return await FormService.submitForm(submissionRequest);
        },
        onSuccess: () => {
            setIsSubmitted(true);
        },
        onError: (error) => {
            console.error('Form submission failed:', error);
        }
    });

    // Helper function to get question by ID from all questions
    const getQuestionById = (questionId: string): AnyQuestion | undefined => {
        return form.questions?.find(q => q.id === questionId);
    };

    // Helper function to get option label by ID
    const getOptionLabel = (question: AnyQuestion, optionId: string): string => {
        if ('options' in question && question.options) {
            const option = question.options.find(opt => opt.id === optionId);
            return option?.label || optionId;
        }
        return optionId;
    };

    // Calculate progress
    const progress = form.questions && form.questions.length > 1
        ? ((currentQuestionIndex + 1) / form.questions.length) * 100
        : 100;

    // Handle next question
    const handleNext = () => {
        if (form.questions && currentQuestionIndex < form.questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    // Handle previous question
    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleSubmit = () => {
        // In preview mode (not public), don't actually submit
        if (!isPublic) {
            setIsSubmitted(true);
            return;
        }

        const answers: FormAnswer[] = [];
        let extractedEmail: string | undefined;
        let extractedPhone: string | undefined;

        // Collect all form answers and convert to human-readable format
        formAnswers.forEach((answer, questionId) => {
            const questionObj = getQuestionById(questionId);
            if (questionObj) {
                let humanReadableAnswer: string | string[] | boolean | number | Media | Media[] = answer;

                // Extract email/phone from purpose fields (first occurrence only)
                if (questionObj.type === QuestionType.SHORT_TEXT) {
                    const shortTextQ = questionObj as ShortTextQuestion;
                    if (shortTextQ.purpose === 'email' && !extractedEmail && typeof answer === 'string') {
                        extractedEmail = answer;
                    } else if (shortTextQ.purpose === 'tel' && !extractedPhone && typeof answer === 'string') {
                        extractedPhone = answer;
                    }
                }

                // Convert option IDs to human-readable labels for choice questions
                if (questionObj.type === QuestionType.MULTIPLE_CHOICE ||
                    questionObj.type === QuestionType.PICTURE_CHOICE ||
                    questionObj.type === QuestionType.DROPDOWN) {
                    if (typeof answer === 'string') {
                        humanReadableAnswer = getOptionLabel(questionObj, answer);
                    } else if (Array.isArray(answer)) {
                        humanReadableAnswer = answer.map(id => getOptionLabel(questionObj, id as string));
                    }
                }
                // For Yes/No questions, keep as boolean
                else if (questionObj.type === QuestionType.YES_NO) {
                    humanReadableAnswer = answer as boolean;
                }
                // For Legal questions, keep as boolean
                else if (questionObj.type === QuestionType.LEGAL) {
                    humanReadableAnswer = answer as boolean;
                }
                // For Rating questions, keep as number
                else if (questionObj.type === QuestionType.RATING) {
                    humanReadableAnswer = answer as number;
                }

                answers.push({
                    questionTitle: questionObj.title,
                    answer: humanReadableAnswer,
                    questionId: questionObj.id,
                    questionDescription: questionObj.description
                });
            }
        });

        // Also include WELCOME screens for the full picture (they don't have user answers)
        (form.questions || []).forEach((question) => {
            if (question.type === QuestionType.WELCOME) {
                answers.unshift({
                    questionTitle: question.title,
                    answer: "-",
                    questionId: question.id,
                    questionDescription: question.description
                });
            }
        });

        const questions = form.questions || [];
        const endScreen = questions.find(q => q.type === QuestionType.END_SCREEN);
        const isEmailRequired = endScreen && 'collectEmail' in endScreen && endScreen.collectEmail;

        // Use EndScreen email if collected, otherwise use extracted email from purpose field
        const finalEmail = (isEmailRequired ? emailValue : undefined) || extractedEmail;

        const submissionRequest: FormSubmission = {
            formId: form.id,
            answers,
            email: finalEmail,
            phone: extractedPhone,
            ...urlParams
        };

        submitFormMutation.mutate(submissionRequest);
    };

    // Render a question based on its type
    const renderQuestion = (question: AnyQuestion) => {
        switch (question.type) {
            case QuestionType.WELCOME:
                return (
                    <Box sx={{textAlign: 'center', p: 4, width: '100%'}}>
                        <Typography variant="h3" sx={{mb: 2}}>
                            {question.title}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            sx={{
                                textTransform: 'none',
                                mt: 2,
                            }}
                            size={'large'}
                        >
                            {question.buttonText || t('Start')}
                        </Button>
                    </Box>
                );

            case QuestionType.MULTIPLE_CHOICE:
                const currentMultipleChoiceValue = question.allowMultiple
                    ? (formAnswers.get(question.id) as string[] || [])
                    : (formAnswers.get(question.id) as string || '');
                return (
                    <Box sx={{p: 4, width: '100%'}}>
                        <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                            {question.title}{question.required && ' *'}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}
                        <FormControl component="fieldset">
                            {question.allowMultiple ? (
                                <Stack>
                                    {question.options && question.options.map((option: any) => (
                                        <FormControlLabel
                                            key={option.id}
                                            control={
                                                <Checkbox
                                                    checked={currentMultipleChoiceValue.includes(option.id)}
                                                    onChange={(e) => {
                                                        const newAnswers = new Map(formAnswers);
                                                        const current = formAnswers.get(question.id) as string[] || [];
                                                        if (e.target.checked) {
                                                            newAnswers.set(question.id, [...current, option.id]);
                                                        } else {
                                                            newAnswers.set(question.id, current.filter(id => id !== option.id));
                                                        }
                                                        setFormAnswers(newAnswers);
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography>
                                                    {option.label}
                                                </Typography>
                                            }
                                            sx={{mb: 1}}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <RadioGroup
                                    value={currentMultipleChoiceValue}
                                    onChange={(e) => {
                                        const newAnswers = new Map(formAnswers);
                                        newAnswers.set(question.id, e.target.value);
                                        setFormAnswers(newAnswers);
                                    }}
                                >
                                    {question.options && question.options.map((option: any) => (
                                        <FormControlLabel
                                            key={option.id}
                                            value={option.id}
                                            control={<Radio/>}
                                            label={
                                                <Typography>
                                                    {option.label}
                                                </Typography>
                                            }
                                            sx={{mb: 2}}
                                        />
                                    ))}
                                </RadioGroup>
                            )}
                        </FormControl>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 4}}>
                            {currentQuestionIndex > 0 && (
                                <Button
                                    variant="outlined"
                                    onClick={handlePrevious}
                                    startIcon={<ArrowBackIcon/>}
                                >
                                    {t('Previous')}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                endIcon={<ArrowForwardIcon/>}
                                disabled={!isCurrentQuestionValid()}
                                sx={{
                                    ml: 'auto',
                                }}
                            >
                                {t('Next')}
                            </Button>
                        </Box>
                    </Box>
                );

            case QuestionType.YES_NO:
                return (
                    <Box sx={{p: 4, textAlign: 'center', width: '100%'}}>
                        <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                            {question.title}{question.required && ' *'}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}
                        <Box sx={{display: 'flex', justifyContent: 'center', gap: 3, mb: 4}}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, true);
                                    setFormAnswers(newAnswers);
                                    handleNext();
                                }}
                                sx={{
                                    '&:hover': {},
                                    width: 120,
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                }}
                            >
                                {t('Yes')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, false);
                                    setFormAnswers(newAnswers);
                                    handleNext();
                                }}
                                sx={{
                                    '&:hover': {},
                                    width: 120,
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                }}
                            >
                                {t('No')}
                            </Button>
                        </Box>
                        {currentQuestionIndex > 0 && (
                            <Button
                                variant="text"
                                onClick={handlePrevious}
                                startIcon={<ArrowBackIcon/>}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                {t('Previous')}
                            </Button>
                        )}
                    </Box>
                );

            case QuestionType.SHORT_TEXT:
            case QuestionType.LONG_TEXT:
                const multiline = question.type === QuestionType.LONG_TEXT;
                const currentTextValue = formAnswers.get(question.id) as string || '';
                const shortTextQ = question as ShortTextQuestion;
                const inputType = shortTextQ.purpose || 'text';

                // Validation for email/tel
                const showValidationError = currentTextValue && question.required;
                let validationError = '';
                if (showValidationError && inputType === 'email' && !!emailValidator(currentTextValue)) {
                    validationError = t('Please enter a valid email address');
                } else if (showValidationError && inputType === 'tel' && !!phoneNumberValidator(currentTextValue)) {
                    validationError = t('Please enter a valid phone number');
                }

                return (
                    <Box sx={{p: 4, width: '100%'}}>
                        <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                            {question.title}{question.required && ' *'}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}
                        {inputType === 'tel' ? (
                            <PhoneInputField
                                value={currentTextValue}
                                onChange={(phone) => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, phone);
                                    setFormAnswers(newAnswers);
                                }}
                                error={!!validationError}
                                helperText={validationError}
                                placeholder={question.placeholder || t('Phone number')}
                                variant="light"
                                sx={{mb: 2, width: '100%'}}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                multiline={multiline}
                                rows={multiline ? 4 : 1}
                                type={multiline ? undefined : inputType}
                                placeholder={question.placeholder}
                                value={currentTextValue}
                                onChange={(e) => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, e.target.value);
                                    setFormAnswers(newAnswers);
                                }}
                                onKeyDown={(e) => {
                                    // Only handle Enter for single-line inputs
                                    if (!multiline && e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleNext();
                                    }
                                }}
                                error={!!validationError}
                                helperText={
                                    validationError || (!multiline && (
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5}}>
                                            <KeyboardReturnIcon sx={{fontSize: 16}}/>
                                            <Typography variant="caption">
                                                {t('Press Enter to submit answer')}
                                            </Typography>
                                        </Box>
                                    ))
                                }
                                sx={{
                                    mb: 2,
                                }}
                            />
                        )}
                        <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 2}}>
                            {currentQuestionIndex > 0 && (
                                <Button
                                    variant="outlined"
                                    onClick={handlePrevious}
                                    startIcon={<ArrowBackIcon/>}
                                >
                                    {t('Previous')}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                endIcon={<ArrowForwardIcon/>}
                                disabled={!isCurrentQuestionValid()}
                                sx={{
                                    ml: 'auto',
                                }}
                            >
                                {t('Next')}
                            </Button>
                        </Box>
                    </Box>
                );

            case QuestionType.PICTURE_CHOICE:
                const currentPictureChoiceValue = question.allowMultiple
                    ? (formAnswers.get(question.id) as string[] || [])
                    : (formAnswers.get(question.id) as string || '');

                return (
                    <Box sx={{p: 4, width: '100%'}}>
                        <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                            {question.title}{question.required && ' *'}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}
                        <Box sx={{mb: 4}}>
                            <Grid container spacing={2}>
                                {question.options && question.options.map((option: any) => {
                                    const isSelected = question.allowMultiple
                                        ? currentPictureChoiceValue.includes(option.id)
                                        : currentPictureChoiceValue === option.id;

                                    return (
                                        (
                                            <Grid xs={6} key={option.id}>
                                                <Card
                                                    key={option.id}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        border: '2px solid',
                                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                                        '&:hover': {
                                                            borderColor: 'primary.light'
                                                        }
                                                    }}
                                                    onClick={() => {
                                                        const newAnswers = new Map(formAnswers);
                                                        if (question.allowMultiple) {
                                                            const current = formAnswers.get(question.id) as string[] || [];
                                                            if (isSelected) {
                                                                newAnswers.set(question.id, current.filter(id => id !== option.id));
                                                            } else {
                                                                newAnswers.set(question.id, [...current, option.id]);
                                                            }
                                                        } else {
                                                            newAnswers.set(question.id, option.id);
                                                            setFormAnswers(newAnswers);
                                                            // Auto-advance for single selection if required
                                                            if (question.required) {
                                                                handleNext();
                                                                return;
                                                            }
                                                        }
                                                        setFormAnswers(newAnswers);
                                                    }}
                                                >
                                                    {option.imageMedia ? (
                                                        <CardMedia
                                                            component="img"
                                                            height="150"
                                                            image={getScaledUrl(option.imageMedia, MediaSize.LARGE_600, MediaSize.LARGE_600)}
                                                            alt={option.label}
                                                            sx={{objectFit: 'cover'}}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: 150,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                backgroundColor: 'grey.100'
                                                            }}
                                                        >
                                                            <AddPhotoAlternateIcon sx={{fontSize: 40, color: 'grey.400'}}/>
                                                        </Box>
                                                    )}
                                                    <Box sx={{p: 1, display: 'flex', alignItems: 'center'}}>
                                                        {question.allowMultiple && (
                                                            <Checkbox checked={isSelected} readOnly size="small"/>
                                                        )}
                                                        <Typography variant="body2" sx={{ml: question.allowMultiple ? 0.5 : 0}}>
                                                            {option.label}
                                                        </Typography>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        )
                                    )
                                })}
                            </Grid>
                        </Box>

                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                            {currentQuestionIndex > 0 && (
                                <Button
                                    variant="outlined"
                                    onClick={handlePrevious}
                                    startIcon={<ArrowBackIcon/>}
                                >
                                    {t('Previous')}
                                </Button>
                            )}
                            {(question.allowMultiple || !question.required) && (
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    endIcon={<ArrowForwardIcon/>}
                                    disabled={!isCurrentQuestionValid()}
                                    sx={{
                                        ml: 'auto',
                                    }}
                                >
                                    {t('Next')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                );

            case QuestionType.DROPDOWN:
                const currentDropdownValue = formAnswers.get(question.id) as string || '';
                return (
                    <Box sx={{p: 4, width: '100%'}}>
                        <Typography variant="h6" sx={{mb: 1}} textAlign={'center'}>
                            {question.title}{question.required && ' *'}
                        </Typography>

                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}

                        <FormControl
                            fullWidth
                            sx={{
                                mb: 4
                            }}
                        >
                            <Select
                                displayEmpty
                                value={currentDropdownValue}
                                onChange={(e) => {
                                    const newAnswers = new Map(formAnswers);
                                    newAnswers.set(question.id, e.target.value as string);
                                    setFormAnswers(newAnswers);
                                }}
                                inputProps={{'aria-label': t('Select option')}}
                                MenuProps={{
                                    disablePortal: true,
                                    PaperProps: {
                                        sx: {
                                            bgcolor: '#fff',
                                            maxHeight: 300,
                                        }
                                    },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    <em>{t('Select an option')}</em>
                                </MenuItem>
                                {question.options && question.options.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                            {currentQuestionIndex > 0 && (
                                <Button
                                    variant="outlined"
                                    onClick={handlePrevious}
                                    startIcon={<ArrowBackIcon/>}
                                >
                                    {t('Previous')}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                endIcon={<ArrowForwardIcon/>}
                                disabled={!isCurrentQuestionValid()}
                                sx={{
                                    ml: 'auto',
                                }}
                            >
                                {t('Next')}
                            </Button>
                        </Box>
                    </Box>
                );

            case QuestionType.LEGAL:
                const currentLegalValue = formAnswers.get(question.id) as boolean || false;
                return (
                    <Box sx={{p: 4, width: '100%'}}>
                        <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                            {question.title}{question.required && ' *'}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}
                        <Paper
                            sx={{
                                p: 3,
                                maxHeight: 200,
                                overflow: 'auto',
                                borderRadius: 1,
                                mb: 3,
                            }}
                        >
                            <MdRenderer>
                                {question.termsText || t('Terms and conditions text.')}
                            </MdRenderer>
                        </Paper>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={currentLegalValue}
                                    onChange={(e) => {
                                        const newAnswers = new Map(formAnswers);
                                        newAnswers.set(question.id, e.target.checked);
                                        setFormAnswers(newAnswers);
                                    }}
                                    sx={{
                                        '&.Mui-checked': {}
                                    }}
                                />
                            }
                            label={
                                <Typography>
                                    {t('I agree')}
                                </Typography>
                            }
                            sx={{
                                mb: 3,
                            }}
                        />

                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                            {currentQuestionIndex > 0 && (
                                <Button
                                    variant="outlined"
                                    onClick={handlePrevious}
                                    startIcon={<ArrowBackIcon/>}
                                >
                                    {t('Previous')}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                endIcon={<ArrowForwardIcon/>}
                                disabled={!isCurrentQuestionValid()}
                                sx={{
                                    ml: 'auto',
                                }}
                            >
                                {t('Next')}
                            </Button>
                        </Box>
                    </Box>
                );

            case QuestionType.RATING:
                const currentRatingValue = formAnswers.get(question.id) as number || 0;
                const maxRating = question.maxRating || 10;
                return (
                    <Box sx={{p: 4, width: '100%', textAlign: 'center'}}>
                        <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                            {question.title}{question.required && ' *'}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}
                        {question.ratingLabel && (
                            <Typography variant="body2" sx={{mb: 4}}>
                                {question.ratingLabel}
                            </Typography>
                        )}
                        <Box sx={{display: 'flex', justifyContent: 'center', mb: 4}}>
                            {maxRating === 5 ? (
                                <Rating
                                    value={currentRatingValue}
                                    max={5}
                                    sx={{
                                        fontSize: '3rem',
                                        '& .MuiRating-iconFilled': {
                                            color: 'primary.main'
                                        },
                                        '& .MuiRating-iconHover': {
                                            color: 'primary.dark'
                                        }
                                    }}
                                    onChange={(_, newValue) => {
                                        const newAnswers = new Map(formAnswers);
                                        newAnswers.set(question.id, newValue || 0);
                                        setFormAnswers(newAnswers);
                                    }}
                                    emptyIcon={<StarIcon style={{opacity: 0.3}} fontSize="inherit"/>}
                                />
                            ) : (
                                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center'}}>
                                    {Array.from({length: maxRating}, (_, i) => (
                                        <Box
                                            key={i}
                                            onClick={() => {
                                                const newAnswers = new Map(formAnswers);
                                                newAnswers.set(question.id, i + 1);
                                                setFormAnswers(newAnswers);
                                            }}
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px solid',
                                                borderColor: currentRatingValue === i + 1 ? 'primary.main' : 'rgba(255, 255, 255, 0.3)',
                                                backgroundColor: currentRatingValue === i + 1 ? 'primary.main' : 'transparent',
                                                borderRadius: 2,
                                                fontSize: '1.1rem',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    backgroundColor: currentRatingValue === i + 1 ? 'primary.dark' : 'rgba(255, 255, 255, 0.1)'
                                                }
                                            }}
                                        >
                                            {i + 1}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                            {currentQuestionIndex > 0 && (
                                <Button
                                    variant="outlined"
                                    onClick={handlePrevious}
                                    startIcon={<ArrowBackIcon/>}
                                >
                                    {t('Previous')}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                endIcon={<ArrowForwardIcon/>}
                                disabled={!isCurrentQuestionValid()}
                                sx={{
                                    ml: 'auto',
                                }}
                            >
                                {t('Next')}
                            </Button>
                        </Box>
                    </Box>
                );

            case QuestionType.UPLOAD_MEDIA:
                const currentUploadValue = formAnswers.get(question.id) as Media | Media[] | undefined;
                return (
                    <Box sx={{p: 4, width: '100%'}}>
                        <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                            {question.title}{question.required && ' *'}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}
                        <PublicMediaUpload
                            value={currentUploadValue}
                            onChange={(media) => {
                                const newAnswers = new Map(formAnswers);
                                if (media) {
                                    newAnswers.set(question.id, media);
                                } else {
                                    newAnswers.delete(question.id);
                                }
                                setFormAnswers(newAnswers);
                            }}
                            allowMultiple={question.allowMultiple}
                            allowedTypes={question.allowedTypes}
                            required={question.required}
                        />
                        <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 4}}>
                            {currentQuestionIndex > 0 && (
                                <Button
                                    variant="outlined"
                                    onClick={handlePrevious}
                                    startIcon={<ArrowBackIcon/>}
                                >
                                    {t('Previous')}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                endIcon={<ArrowForwardIcon/>}
                                disabled={!isCurrentQuestionValid()}
                                sx={{
                                    ml: 'auto',
                                }}
                            >
                                {t('Next')}
                            </Button>
                        </Box>
                    </Box>
                );

            case QuestionType.END_SCREEN:
                const isEmailRequired = question.collectEmail;
                const isEmailValidForSubmit = !isEmailRequired || (isEmailRequired && !emailValidator(emailValue));

                if (isSubmitted) {
                    const SubmittedScreen = () => {
                        const waitSeconds = question.redirectWaitSeconds ?? 5;
                        const [countdown, setCountdown] = useState(waitSeconds);

                        useEffect(() => {
                            if (isPublic && question.redirectUrl) {
                                // Immediate redirect if wait time is 0 or negative
                                if (waitSeconds <= 0) {
                                    window.location.href = question.redirectUrl;
                                    return;
                                }

                                const timer = setInterval(() => {
                                    setCountdown((prev) => {
                                        if (prev <= 1) {
                                            clearInterval(timer);
                                            window.location.href = question.redirectUrl!;
                                            return 0;
                                        }
                                        return prev - 1;
                                    });
                                }, 1000);

                                return () => clearInterval(timer);
                            }
                        }, []);

                        return (
                            <Box sx={{textAlign: 'center', p: 4, width: '100%'}}>
                                <CheckCircleIcon
                                    sx={{
                                        fontSize: '80px',
                                        mb: 3
                                    }}
                                />
                                <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                                    {t('Thank You!')}
                                </Typography>
                                <Typography variant="subtitle1" sx={{mb: 4}}>
                                    {t('Your form has been submitted successfully.')}
                                    {isPublic && question.redirectUrl && waitSeconds > 0 && (
                                        <><br/>{t('Redirecting in {{countdown}} seconds...', {countdown})}</>
                                    )}
                                </Typography>
                            </Box>
                        );
                    };

                    return <SubmittedScreen/>;
                }

                return (
                    <Box sx={{textAlign: 'center', p: 4, width: '100%'}}>
                        <Typography variant="h6" sx={{mb: 2}} textAlign={'center'}>
                            {question.title}
                        </Typography>
                        {question.description && (
                            <Stack sx={{mb: 3, alignItems: 'center'}}>
                                <MdRenderer>
                                    {question.description}
                                </MdRenderer>
                            </Stack>
                        )}

                        <Stack gap={2} alignItems={'center'}>
                            {/* Email Input - Only show if collectEmail is enabled */}
                            {question.collectEmail && (
                                <TextField
                                    fullWidth
                                    type="email"
                                    value={emailValue}
                                    onChange={(e) => setEmailValue(e.target.value)}
                                    placeholder={question.emailPlaceholder || t('Enter your email address')}
                                    sx={{
                                        maxWidth: 400
                                    }}
                                    error={isEmailRequired && emailValue !== '' && !!emailValidator(emailValue)}
                                    helperText={isEmailRequired && emailValue !== '' && !!emailValidator(emailValue) ? t('Please enter a valid email address') : ''}
                                    FormHelperTextProps={{
                                        sx: {color: '#f44336'}
                                    }}
                                />
                            )}

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={!isEmailValidForSubmit || submitFormMutation.isLoading}
                                sx={{
                                    textTransform: 'none',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    minWidth: 120
                                }}
                            >
                                {submitFormMutation.isLoading ? (
                                    <CircularProgress size={20} sx={{color: 'inherit'}}/>
                                ) : (
                                    question.buttonText || t('Submit')
                                )}
                            </Button>

                            {currentQuestionIndex > 0 && (
                                <Button
                                    variant="text"
                                    onClick={handlePrevious}
                                    startIcon={<ArrowBackIcon/>}
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                    }}
                                >
                                    {t('Previous')}
                                </Button>
                            )}
                        </Stack>
                    </Box>
                );

            default:
                return (
                    <Box sx={{p: 4, width: '100%', textAlign: 'center'}}>
                        <Typography variant="h5">
                            {t('Unsupported question type:')} {(question as AnyQuestion).type}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            sx={{mt: 2}}
                        >
                            {t('Next')}
                        </Button>
                    </Box>
                );
        }
    };

    // Helper function to parse formMinHeight and ensure proper unit
    const getMinHeight = (): string | undefined => {
        if (!isEmbedded || !mergedTheme?.shape?.formMinHeight) {
            return undefined;
        }

        const value = mergedTheme.shape.formMinHeight;

        // If it's a number (no unit), treat as pixels
        if (/^\d+$/.test(value)) {
            return `${value}px`;
        }

        // Otherwise use the value as-is (should have units like px, vh, %, etc.)
        return value;
    };

    return (
        <FormThemeProvider theme={mergedTheme}>
            <Box sx={{
                height: isEmbedded ? undefined : '100vh',
                width: isEmbedded ? undefined : '100vw',
                position: isEmbedded ? undefined : 'fixed',
                top: 0,
                left: 0,
            }}>
                {!isPublic && !isEmbedded && (
                    <AppBar position="static" color="default" elevation={0}>
                        <Toolbar>
                            <Typography variant="h6" sx={{flexGrow: 1}}>
                                {form.title}
                            </Typography>
                            {onClose && (
                                <Button
                                    color="error"
                                    size="large"
                                    onClick={onClose}
                                    aria-label={t('Close Preview')}
                                    startIcon={<CloseIcon/>}
                                    variant="outlined"
                                >
                                    {t('Close Preview')}
                                </Button>
                            )}
                        </Toolbar>
                    </AppBar>
                )}

                {/* Progress Bar */}
                {form.settings?.showProgressBar && (
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 8,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: 'primary.main',
                            }
                        }}
                    />
                )}

                {/* Preview Content */}
                <Box
                    sx={{
                        minHeight: getMinHeight(),
                        height: isEmbedded ? undefined : (isPublic ? '100vh' : 'calc(100vh - 72px)'),
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: isEmbedded ? undefined : 'grey.50',
                        // overflow: 'visible',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                    }}
                >
                    {/* Split Layout Container */}
                    {isSplitLayout && (backgroundImageUrl || isWelcomeWithVideoAndSplit) ? (
                        <Box
                            width={'100%'}
                            height={'100%'}
                            sx={{
                                display: 'flex',
                                flexDirection: layoutStyles.flexDirection,
                                position: 'relative',
                                zIndex: 2
                            }}
                        >
                            {/* Split Layout: Image/Video Section */}
                            <Box
                                sx={{
                                    width: effectiveLayout === 'TOP_SPLIT' ? '100%' : layoutStyles.imageSize,
                                    height: effectiveLayout === 'TOP_SPLIT' ? layoutStyles.imageSize : undefined,
                                    backgroundImage: isWelcomeWithVideoAndSplit ? undefined : `url(${backgroundImageUrl})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                {/* Video in Split Side - Special case for welcome with video */}
                                {isWelcomeWithVideoAndSplit && welcomeVideoMedia && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            '& video': {
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            },
                                            '& iframe': {
                                                width: '100%',
                                                height: '100%'
                                            },
                                            '& > div': {
                                                width: '100%',
                                                height: '100%'
                                            }
                                        }}
                                    >
                                        {/*<VideoPlayer*/}
                                        {/*    media={welcomeVideoMedia}*/}
                                        {/*    autoPlay={true}*/}
                                        {/*    loop={true}*/}
                                        {/*    muted={true}*/}
                                        {/*    controls={false}*/}
                                        {/*    style={{*/}
                                        {/*        width: '100%',*/}
                                        {/*        height: '100%',*/}
                                        {/*        maxWidth: '100%',*/}
                                        {/*        maxHeight: '100%',*/}
                                        {/*        objectFit: 'cover'*/}
                                        {/*    }}*/}
                                        {/*/>*/}
                                    </Box>
                                )}
                            </Box>

                            {/* Content Section */}
                            <Box
                                sx={{
                                    width: effectiveLayout === 'TOP_SPLIT' ? '100%' : layoutStyles.contentSize,
                                    height: effectiveLayout === 'TOP_SPLIT' ? layoutStyles.contentSize : '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'relative',
                                    minHeight: getMinHeight()
                                }}
                            >
                                {/* Theme Background Layer */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'background.paper',
                                        zIndex: 1,
                                    }}
                                />

                                {/* Content Layer */}
                                <Box sx={{position: 'relative', zIndex: 2, width: '100%'}}>
                                    {currentQuestion && renderQuestion(currentQuestion)}
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {/* Video Background Layer (absolute bottom) - Only for Welcome screens with video */}
                            {isWelcomeWithVideo && welcomeVideoMedia && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 0,
                                        '& video': {
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        },
                                        '& iframe': {
                                            width: '100%',
                                            height: '100%'
                                        },
                                        '& > div': {
                                            width: '100%',
                                            height: '100%'
                                        }
                                    }}
                                >
                                    {/*<VideoPlayer*/}
                                    {/*    media={welcomeVideoMedia}*/}
                                    {/*    autoPlay={true}*/}
                                    {/*    loop={true}*/}
                                    {/*    muted={true}*/}
                                    {/*    controls={false}*/}
                                    {/*    style={{*/}
                                    {/*        width: '100%',*/}
                                    {/*        height: form.settings?.showProgressBar ? `calc(100% - 8px` : '100%',*/}
                                    {/*        maxWidth: '100%',*/}
                                    {/*        maxHeight: '100%',*/}
                                    {/*        objectFit: 'cover'*/}
                                    {/*    }}*/}
                                    {/*/>*/}
                                </Box>
                            )}

                            {/* Background Image Layer (bottom) - Only for DEFAULT layout */}
                            {!isSplitLayout && backgroundImageUrl && !isWelcomeWithVideo && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundImage: `url(${backgroundImageUrl})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        zIndex: 0,
                                    }}
                                />
                            )}

                            {/* Theme Background Layer (middle) */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: isWelcomeWithVideo ? 'rgba(0, 0, 0, 0.3)' : 'background.paper',
                                    zIndex: 1,
                                }}
                            />

                            {/* Content Layer (top) */}
                            <Box maxWidth={800} width={'100%'} sx={{
                                position: 'relative',
                                zIndex: 2,
                                ...(isWelcomeWithVideo && {
                                    '& .MuiTypography-root': {
                                        color: '#FFFFFF',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                                    }
                                })
                            }}>
                                {currentQuestion && renderQuestion(currentQuestion)}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </FormThemeProvider>
    );
};

export default PreviewComponent;
